import { ControlMetadata } from 'app/extensibility/metadatas/control/control-metadata';
import { ILinkedServiceTypeMetadata } from 'app/extensibility/metadatas/resource-type/linked-service-type-metadata';

import { GlobalVariables } from 'app/services/globals';
import { format } from 'app/shared/utils/string.util';
import { driverVersionVersionOptions, driverVersions } from '../consts/tabular-consts';
import { SSLMode, sslModeOptions } from '../consts/postgre-consts';
import { greenplumAdditionalFieldsKeyOptions } from '../consts/greenplum-const';
import { DataSourceOptionKind, CommonResourceKind as ResourceKind } from '@powerquery/mashup-editor-client';
import { ConnectionAuthKind } from 'shell-models-lib';
import { adfModelTypeMap } from '../consts/adf-model-type-map-const';

function commonControls(isOld: boolean): ControlMetadata[] {
    return [
        {
            type: 'textbox',
            bind: 'host',
            label: isOld ? stringResources.ModelName_Host : stringResources.ModelName_ServerName,
            required: true,
            validators: [
                { type: 'required', errorMessage: '' }
            ],
            isLabelVertical: true
        },
        {
            type: 'textbox-number',
            bind: 'port',
            label: stringResources.ModelName_Port,
            isLabelVertical: true,
            inputType: 'number',
            defaultValue: isOld ? undefined : 5432,
            min: 0
        },
        {
            type: 'textbox',
            bind: 'databaseName',
            label: stringResources.ModelName_DatabaseName,
            required: true,
            validators: [
                { type: 'required', errorMessage: '' }
            ],
            isLabelVertical: true
        },
        {
            type: 'textbox',
            bind: 'userName',
            label: stringResources.ModelName_UserName,
            required: true,
            validators: [
                { type: 'required', errorMessage: '' }
            ],
            isLabelVertical: true
        },
        {

            type: 'dynamic-akv-switcher',
            hideAKVSwitcherInConnStrProps: true,
            bind: 'password',
            label: stringResources.ModelName_Password,
            child: {
                type: 'password',
                label: stringResources.ModelName_Password,
                required: true,
                isLabelVertical: true,
                validators: [
                    { type: 'required', errorMessage: '' }
                ]
            },
            description: format(stringResources.AzureKeyVaultSecretName_Tooltip_Template,
                format(stringResources.ModelName_LinkedServiceTemplate, stringResources.Greenplum_DisplayText),
                stringResources.ModelName_Password)
        },
        isOld ? null : {
            type: 'select',
            bind: 'sslMode',
            label: stringResources.SSL_Mode,
            options: sslModeOptions,
            isLabelVertical: true,
            defaultValue: SSLMode.Require
        },
        isOld ? {
            type: 'key-val-list',
            bind: 'additionalFields',
            label: stringResources.AdditionalConnectionStringFields
        } : {
            type: 'key-val-list',
            bind: 'additionalFields',
            label: stringResources.AdditionalConnectionStringFields,
            keyOptions: greenplumAdditionalFieldsKeyOptions,
            keyType: 'dropdown'
        }
    ].filter(Boolean) as ControlMetadata[];
}
const v1Contorls: ControlMetadata[] = [
    {
        type: 'select-button-switch',
        required: true,
        isLabelVertical: true,
        options: [
            {
                label: stringResources.ModelName_ConnectionString,
                value: false
            },
            {
                label: stringResources.AzureKeyVault_DisplayText,
                value: true,
                hideAKVOptionIfNotChosen: false
            }
        ],
        bind: 'enableAKV',
        isNotRelatedToCredential: true,
        defaultValue: false,
        cases: [
            {
                caseValue: true,
                children: [
                    {
                        type: 'azure-key-vault',
                        bind: 'referedAKV',
                        isLabelVertical: true
                    }
                ]
            },
            {
                caseValue: false,
                children: commonControls(true)
            }
        ]
    }
];

export const greenplumMetadata: ILinkedServiceTypeMetadata = {
    type: adfModelTypeMap.greenplum.linkedServiceType,
    displayName: stringResources.Greenplum_DisplayText,
    linkedServiceKind: 'table',
    icon: {
        className: 'svg-i-100x100-greenplum'
    },
    isFirstClass: true,
    category: ['Database'],
    isDeprecated: !GlobalVariables.showLegacy ? '{ version !== "v2" }' : false,
    supportDatasetV2: true,
    showWarningMessage: '{ version !== "v2" }',
    warningMessage: format(
        stringResources.Connector_Legacy_IsNotSupportedWarning_alt5,
        stringResources.VersionLabel.toLocaleLowerCase()
    ),
    warningMessageHyperlink: 'https://learn.microsoft.com/azure/data-factory/connector-greenplum',
    copySinkTypes: [],
    copySourceTypes: [adfModelTypeMap.greenplum.copySourceType],
    supportedInTrident: true,
    supportCopyJobSource: true,
    supportMultipleIncrementalColumns: true,
    pqoDataSourceOption: {
        kind: DataSourceOptionKind.Extension,
        resourceKind: 'GreenplumForPipeline' as ResourceKind
    },
    supportedAuthKindsInTrident: [ConnectionAuthKind.Basic],
    supportOPDG: true,
    SHIRVersion: {
        v2: '5.52.00'
    },
    controls: [
        {
            type: 'radio-switch',
            bind: 'version',
            label: stringResources.VersionLabel,
            isLabelVertical: true,
            required: true,
            options: driverVersionVersionOptions,
            defaultValue: driverVersions.v2,
            hide: GlobalVariables.showLegacy ? false : '{version === "v2" }',
            cases: [
                {
                    caseValue: driverVersions.v1,
                    children:
                        [
                            {
                                type: 'hint',
                                hintMode: 'warning',
                                description: format(
                                    stringResources.Connector_Legacy_IsNotSupportedWarning_alt6,
                                    stringResources.VersionLabel.toLocaleLowerCase()),
                                link: 'https://learn.microsoft.com/azure/data-factory/connector-greenplum'
                            },
                            ...v1Contorls
                        ]
                },
                {
                    caseValue: driverVersions.v2,
                    children: commonControls(false)
                }
            ]
        },
        ...commonControls(false).map((control) => {
            return {
                ...control,
                hide: !GlobalVariables.showLegacy ? '{version !== "v2" }' : true
            };
        })
    ]
};
